import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Button,
  ContentBox,
  ContentPage,
  DrawerFilterPanel,
  FullTable,
  ModalDefault,
  NoResults,
  Notice,
  PageActions,
  Select,
  TableFilters,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import RejectHoursModal from "@components/RejectHoursModal";
import { ADJUSTMENT_COLUMNS } from "@components/ClockAdjustments";
import { escapeHtml, usePageHeader } from "@components/ow";
import { hasPermission } from "@helpers/permissions";
import { clockDateParts } from "@helpers/timeClock";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";
import { employeeOptionsOf } from "./employeeOptions";
import DirectAdjustmentModal from "./DirectAdjustmentModal";

// Ajustes de ponto (modelo Lista do DS OneWork): os pedidos de inclusão de batida dos
// colaboradores CLT, com aprovar e reprovar na linha, e o ajuste direto do gestor.
// Para o ADM e o gestor de ponto (time_clock:read); cada ação tem a sua permissão.

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "PENDING" },
  { label: "Aprovado", value: "APPROVED" },
  { label: "Reprovado", value: "REJECTED" },
];

// Abre nos pendentes, como a tela antiga
const INITIAL_FILTERS = { status: STATUS_DATA[1] };

const BREADCRUMB = [
  { page: "Ponto CLT", route: "/c/time-clock" },
  { page: "Ajustes de ponto", route: "/c/time-clock/adjustments" },
];

// Aprovar e Reprovar sempre lado a lado, mesmo com a coluna apertada
const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

// Respiro entre o aviso e os filtros, dentro do mesmo ContentBox
const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

const Help = styled.span`
  cursor: help;
`;

export default function TimeClockAdjustments() {
  const timeClockService = useService("timeClock");
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth.role === "ADM";
  // Cada ação é um item separado na tela de permissões
  const canApprove = hasPermission("time_clock:approve");
  const canReject = hasPermission("time_clock:reject");
  const canCreateDirect = hasPermission("time_clock:create_adjustment");

  usePageHeader({ title: "Ajustes de ponto", breadcrumb: BREADCRUMB });

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({ totalRegisters: 0, list: [], totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [toApprove, setToApprove] = useState(null);
  const [toReject, setToReject] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [showDirect, setShowDirect] = useState(false);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  } = useListFilters(INITIAL_FILTERS, setPagination);

  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim();
  const contractValue = filters.contract || "";

  useEffect(() => {
    fetchAdjustments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, searchTerm, contractValue]);

  useEffect(() => {
    (async () => {
      const result = await timeClockService.listCltContracts();
      const data = result && !result.error ? result.data?.list || result.data : null;
      // Sem a lista, o filtro de colaborador só não aparece
      setContracts(Array.isArray(data) ? data : []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildQuery() {
    // Sem scope=company, o gestor de ponto recebe só os próprios pedidos
    const params = ["scope=company", `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (statusValue) params.push(`status=${statusValue}`);
    if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
    if (contractValue) params.push(`contract_id=${contractValue}`);
    return params.join("&");
  }

  async function fetchAdjustments() {
    const request = ++lastRequest.current;
    setLoading(true);
    const result = await timeClockService.listAdjustments(buildQuery());
    if (request !== lastRequest.current) return;
    // Em erro, o sendError do service já mostrou o toast
    setPaginationData(
      result && !result.error && result.data?.list ? result.data : { totalRegisters: 0, list: [], totalPages: 1 }
    );
    setLoading(false);
  }

  // Limpa busca e colaborador e volta aos pendentes (o "Limpar filtros" do vazio)
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: INITIAL_FILTERS.status });
  }

  async function approve() {
    setDeciding(true);
    const result = await timeClockService.approveAdjustment(toApprove.id);
    setDeciding(false);
    // Em erro, o sendError do service já mostrou o toast; o modal continua aberto
    if (!result || result.error) return;
    toast.success("Ajuste aprovado! A batida foi incluída no espelho.");
    setToApprove(null);
    fetchAdjustments();
  }

  async function reject(reason) {
    setDeciding(true);
    const result = await timeClockService.rejectAdjustment(toReject.id, reason);
    setDeciding(false);
    if (!result || result.error) return;
    toast.success("Ajuste reprovado");
    setToReject(null);
    fetchAdjustments();
  }

  /** O gestor não decide o próprio pedido (o back também barra). */
  const isOwnRequest = (adjustment) => !isAdmin && adjustment.user_id === auth.id;

  function actionsCell(adjustment) {
    if (adjustment.status !== "PENDING" || !(canApprove || canReject)) return null;
    if (isOwnRequest(adjustment)) {
      return (
        <Tooltip
          content="Sua solicitação: outro gestor ou um administrador decide."
          $position="top"
          $size="medium"
          $color="dark">
          <Help>
            <Text $text="—" $variant="paragraph_small_medium" $color="N400" />
          </Help>
        </Tooltip>
      );
    }
    return (
      <Actions>
        {canApprove ? (
          <Button
            $text="Aprovar"
            $variant="outline"
            $size="small"
            $noWidth
            $action={() => setToApprove(adjustment)}
          />
        ) : null}
        {canReject ? (
          <Button
            $text="Reprovar"
            $variant="outline_danger"
            $size="small"
            $noWidth
            $action={() => setToReject(adjustment)}
          />
        ) : null}
      </Actions>
    );
  }

  const columns = [
    {
      id: "requester",
      header: "Colaborador",
      size: 220,
      maxSize: 280,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.requester?.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          {row.original.requester?.email ? (
            <Text $text={escapeHtml(row.original.requester.email)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    ...ADJUSTMENT_COLUMNS,
    ...(canApprove || canReject
      ? [{ id: "actions", size: 190, maxSize: 190, align: "right", cell: ({ row }) => actionsCell(row.original) }]
      : []),
  ];

  const employeeOptions = employeeOptionsOf(contracts);

  const isInitial = statusValue === INITIAL_FILTERS.status.value && !hasFilter && !searchTerm;
  const noResults = isInitial ? (
    <NoResults notResultText="Nenhum ajuste pendente" />
  ) : !statusValue && !hasFilter && !searchTerm ? (
    <NoResults notResultText="Nenhum ajuste de ponto por aqui" />
  ) : (
    <NoResults notResultText="Nenhum ajuste encontrado" includeText="Limpar filtros" includeAction={clearAll} />
  );

  const summary = (adjustment) => {
    const { date, time } = clockDateParts(adjustment.requested_clock_time);
    return {
      text: "Batida de <strong>%s às %s</strong> para <strong>%s</strong>. Justificativa: %s",
      replaces: [date, time, escapeHtml(adjustment.requester?.name || "o colaborador"), escapeHtml(adjustment.reason || "-")],
    };
  };

  return (
    <ContentPage>
      {canCreateDirect ? (
        <PageActions>
          <Button $text="Criar ajuste" $variant="primary" $action={() => setShowDirect(true)} />
        </PageActions>
      ) : null}

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Pedidos de inclusão de batida enviados pelos colaboradores. Ao aprovar, a batida entra no espelho e as originais não mudam."
          />
        </NoticeSpace>

        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar por nome ou e-mail"
          statusData={STATUS_DATA}
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableExport
          hasFilter={hasFilter}
        />

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          noResults={noResults}
        />
      </ContentBox>

      <DrawerFilterPanel
        $showDrawer={openFilter}
        $setShowDrawer={() => setOpenFilter((prev) => !prev)}
        $confirmFilter={handleConfirmFilter}
        $clearFilters={handleClearFilters}
        $hasFilter={hasFilter}>
        <Select
          $idForm="adjustments-employee"
          $label="Colaborador"
          $options={employeeOptions}
          $textNoOptions="Nenhum colaborador encontrado"
          placeholder="Todos os colaboradores"
          isClearable
          formatOptionLabel={labelWithHint}
          filterOption={searchLabelAndHint}
          value={employeeOptions.find((option) => option.value === filter.contract) || null}
          onChange={(option) => handleSetFilters(null, "contract", option?.value || "")}
        />
      </DrawerFilterPanel>

      {toApprove ? (
        <ModalDefault
          $showModal
          $setShowModal={() => !deciding && setToApprove(null)}
          $noClose={deciding}
          $size="medium"
          $title="Aprovar ajuste?"
          $contentBackground="N0"
          $footer={
            <>
              <Button
                $text="Cancelar"
                $variant="outline"
                $noWidth
                $isDisabled={deciding}
                $action={() => setToApprove(null)}
              />
              <Button $text="Aprovar ajuste" $variant="primary" $noWidth $isLoading={deciding} $action={approve} />
            </>
          }>
          <Text
            $text={`${summary(toApprove).text}<br/><br/>A batida entra no espelho como ajuste; as originais não mudam.`}
            $replaces={summary(toApprove).replaces}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
        </ModalDefault>
      ) : null}

      {toReject ? (
        <RejectHoursModal
          title="Reprovar ajuste"
          summary={summary(toReject)}
          loading={deciding}
          idForm="adjustment-rejection-reason"
          description="O colaborador vê este texto."
          maxLength={null}
          onConfirm={reject}
          onClose={() => setToReject(null)}
        />
      ) : null}

      {showDirect ? (
        <DirectAdjustmentModal
          onClose={() => setShowDirect(false)}
          onSaved={() => {
            setShowDirect(false);
            fetchAdjustments();
          }}
        />
      ) : null}
    </ContentPage>
  );
}
